import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, TrendingUp, Eye, EyeOff, Mail, Send, Trash2, Inbox } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useFlags } from "../lib/flags";
import api from "../lib/api";
import AdminOnboarding from "../components/AdminOnboarding";
import AdminEventSubmissions from "../components/AdminEventSubmissions";
import AdminContentCreator from "../components/AdminContentCreator";
import AdminDiscovery from "../components/AdminDiscovery";

function StatCard({ label, value, accent }) {
    return (
        <div className="border border-cp-border p-6 md:p-8 bg-cp-ivoire cp-card-hover">
            <p className="cp-kicker text-cp-bleu-gris mb-4">{label}</p>
            <p className={`cp-serif text-5xl md:text-6xl leading-none ${accent ? "text-cp-foret" : "text-cp-noir"}`}>{value}</p>
        </div>
    );
}

export default function AdminDashboard() {
    const { email, loading, logout } = useAuth();
    const { flags, refresh: refreshFlags } = useFlags();
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [newsletter, setNewsletter] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [flagSaving, setFlagSaving] = useState("");
    const [campForm, setCampForm] = useState({ subject: "", preview_text: "", html_body: "" });
    const [campStatus, setCampStatus] = useState("idle");
    const [campMsg, setCampMsg] = useState("");

    const reloadAll = () => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/booking-leads").then((r) => setLeads(r.data.slice(0, 10))).catch(() => {});
        api.get("/festival/waitlist").then((r) => setWaitlist(r.data.slice(0, 10))).catch(() => {});
        api.get("/newsletter").then((r) => setNewsletter(r.data.slice(0, 10))).catch(() => {});
        api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
        api.get("/admin/newsletter/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    };

    useEffect(() => { if (email) reloadAll(); }, [email]);

    const toggleFlag = async (key, newVal) => {
        setFlagSaving(key);
        try {
            await api.post("/settings/flags", { flags: { [key]: newVal } });
            refreshFlags();
        } finally {
            setFlagSaving("");
        }
    };

    if (loading) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;
    if (!email) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-cp-craie" data-testid="admin-dashboard">
            <header className="bg-cp-ivoire border-b border-cp-border">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="cp-serif text-2xl leading-none text-cp-noir">Côte Perdue <span className="cp-kicker text-cp-gris text-[10px] ml-2">Admin</span></Link>
                    <div className="flex items-center gap-6">
                        <p className="text-sm text-cp-gris">{email}</p>
                        <button onClick={logout} data-testid="admin-logout" className="cp-kicker text-[10px] text-cp-noir flex items-center gap-2 hover:text-cp-foret">
                            <LogOut size={14} strokeWidth={1.5} /> Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
                <div className="flex items-center justify-between mb-10">
                    <h1 className="cp-serif text-4xl md:text-5xl leading-tight text-cp-noir">
                        Tableau de bord
                    </h1>
                    <div className="flex items-center gap-6">
                        <Link to="/admin/photos" data-testid="admin-photos-link" className="cp-kicker text-[10px] text-cp-foret hover:text-cp-noir border-b border-cp-foret pb-0.5">
                            Gérer les photos →
                        </Link>
                        <p className="cp-kicker text-cp-foret flex items-center gap-2"><TrendingUp size={14} strokeWidth={1.5} /> Vue d'ensemble</p>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-12" data-testid="admin-stats">
                        <StatCard label="Newsletter" value={stats.newsletter} accent />
                        <StatCard label="Festival waitlist" value={stats.festival_waitlist} accent />
                        <StatCard label="Leads booking" value={stats.booking_leads} />
                        <StatCard label="Événements à venir" value={stats.events_upcoming} />
                        <StatCard label="Adresses" value={stats.places} />
                        <StatCard label="Villes" value={stats.cities} />
                        <StatCard label="Articles" value={stats.articles} />
                        <StatCard label="Balades" value={stats.walks} />
                        <StatCard label="Événements total" value={stats.events_total} />
                        <StatCard label="Nouveaux leads" value={stats.booking_leads_new} accent />
                    </div>
                )}

                {/* Feature flags — visibility toggles */}
                <section className="bg-cp-ivoire border border-cp-border p-8 mb-12" data-testid="admin-flags">
                    <div className="flex items-center justify-between mb-6">
                        <p className="cp-kicker text-cp-foret">Visibilité des sections</p>
                        <p className="cp-tiny text-cp-gris italic">Désactivez une section pour la masquer du site public (header + home).</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-flag-list">
                        {Object.entries(flags).map(([key, on]) => {
                            const label = {
                                "cote-secrete": "Côte Secrète", club: "Cercle / Club", festival: "Festival",
                                magazine: "Magazine", concierge: "Concierge IA", loger: "Loger",
                                agenda: "Agenda", balades: "Balades", parcours: "Parcours",
                                adresses: "Adresses", destinations: "Destinations", experiences: "Expériences",
                            }[key] || key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => toggleFlag(key, !on)}
                                    disabled={flagSaving === key}
                                    data-testid={`flag-toggle-${key}`}
                                    className={`flex items-center justify-between gap-3 px-4 py-3 border transition-colors text-left ${on ? "border-cp-foret bg-cp-foret/5 text-cp-noir" : "border-cp-border bg-cp-craie text-cp-gris"}`}
                                >
                                    <span className="cp-serif text-sm">{label}</span>
                                    {on
                                        ? <Eye size={14} strokeWidth={1.5} className="text-cp-foret shrink-0" />
                                        : <EyeOff size={14} strokeWidth={1.5} className="text-cp-gris shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <section className="bg-cp-ivoire border border-cp-border p-8">
                        <p className="cp-kicker text-cp-foret mb-5">Newsletter — dernières inscriptions</p>
                        {newsletter.length === 0 ? (
                            <p className="text-cp-gris italic">Aucune inscription.</p>
                        ) : (
                            <ul className="divide-y divide-cp-border">
                                {newsletter.map((n) => (
                                    <li key={n.id} className="py-3 flex justify-between items-center text-sm">
                                        <span className="text-cp-noir">{n.email}</span>
                                        <span className="cp-kicker text-[9px] text-cp-gris">{n.source || "—"}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    <section className="bg-cp-ivoire border border-cp-border p-8">
                        <p className="cp-kicker text-cp-foret mb-5">Festival — liste d'attente</p>
                        {waitlist.length === 0 ? (
                            <p className="text-cp-gris italic">Aucune inscription.</p>
                        ) : (
                            <ul className="divide-y divide-cp-border">
                                {waitlist.map((w) => (
                                    <li key={w.id} className="py-3 flex justify-between items-center text-sm">
                                        <span className="text-cp-noir">{w.full_name || w.email}</span>
                                        <span className="cp-kicker text-[9px] text-cp-gris">{w.profile || "—"}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    <section className="bg-cp-ivoire border border-cp-border p-8 lg:col-span-2">
                        <p className="cp-kicker text-cp-foret mb-5">Leads booking</p>
                        {leads.length === 0 ? (
                            <p className="text-cp-gris italic">Aucun lead pour l'instant.</p>
                        ) : (
                            <ul className="divide-y divide-cp-border">
                                {leads.map((l) => (
                                    <li key={l.id} className="py-4 flex flex-col md:flex-row md:justify-between md:items-center gap-2 text-sm">
                                        <div>
                                            <p className="text-cp-noir font-medium">{l.full_name} · {l.email}</p>
                                            <p className="text-cp-gris text-xs">{l.message?.slice(0, 120)}</p>
                                        </div>
                                        <span className="cp-kicker text-[9px] text-cp-bleu-gris">{l.status}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* ===== CONTACTS RECEIVED ===== */}
                    <section className="bg-cp-ivoire border border-cp-border p-8 lg:col-span-2" data-testid="admin-contacts">
                        <div className="flex items-center justify-between mb-5">
                            <p className="cp-kicker text-cp-foret flex items-center gap-2"><Inbox size={14} strokeWidth={1.5}/> Messages contact reçus ({contacts.filter(c => !c.read).length} non lus)</p>
                        </div>
                        {contacts.length === 0 ? (
                            <p className="text-cp-gris italic">Aucun message reçu.</p>
                        ) : (
                            <ul className="divide-y divide-cp-border">
                                {contacts.slice(0, 15).map((c) => (
                                    <li key={c.id} data-testid={`contact-${c.id}`} className={`py-4 flex flex-col gap-2 ${!c.read ? "bg-cp-foret/5 -mx-3 px-3" : ""}`}>
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="flex-1">
                                                <p className="text-cp-noir font-medium">{c.name} <span className="text-cp-gris font-normal">· {c.email}</span></p>
                                                <p className="cp-serif italic text-cp-noir mt-1">{c.subject}</p>
                                                <p className="text-cp-gris text-sm mt-1 line-clamp-2">{c.message}</p>
                                            </div>
                                            <div className="flex flex-col gap-2 shrink-0">
                                                <span className="cp-kicker text-[9px] text-cp-gris">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                                                <div className="flex gap-2">
                                                    {!c.read && (
                                                        <button
                                                            onClick={async () => { await api.put(`/admin/contacts/${c.id}?read=true`); reloadAll(); }}
                                                            data-testid={`mark-read-${c.id}`}
                                                            className="cp-tiny text-cp-foret hover:underline"
                                                        >Marquer lu</button>
                                                    )}
                                                    <button
                                                        onClick={async () => { if (confirm("Supprimer ce message ?")) { await api.delete(`/admin/contacts/${c.id}`); reloadAll(); } }}
                                                        data-testid={`delete-contact-${c.id}`}
                                                        className="text-red-700 hover:opacity-70"
                                                    ><Trash2 size={12}/></button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* ===== NEWSLETTER CAMPAIGNS ===== */}
                    <section className="bg-cp-ivoire border border-cp-border p-8 lg:col-span-2" data-testid="admin-newsletter-campaigns">
                        <div className="flex items-center justify-between mb-5">
                            <p className="cp-kicker text-cp-foret flex items-center gap-2"><Mail size={14} strokeWidth={1.5}/> Newsletter — campagnes ({newsletter.length} abonnés)</p>
                            <p className="cp-tiny text-cp-gris italic">Vous validez chaque envoi.</p>
                        </div>

                        {/* Compose form */}
                        <form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                setCampStatus("loading"); setCampMsg("");
                                try {
                                    await api.post("/admin/newsletter/campaigns", campForm);
                                    setCampForm({ subject: "", preview_text: "", html_body: "" });
                                    setCampStatus("created");
                                    setCampMsg("Brouillon créé · cliquez « Envoyer » pour diffuser.");
                                    reloadAll();
                                } catch (err) {
                                    setCampStatus("error");
                                    setCampMsg(err?.response?.data?.detail || "Erreur");
                                }
                            }}
                            className="bg-cp-craie border border-cp-border p-4 mb-6 space-y-3"
                            data-testid="campaign-form"
                        >
                            <input
                                required
                                placeholder="Sujet de la newsletter (visible dans l'email)"
                                value={campForm.subject}
                                onChange={(e) => setCampForm({ ...campForm, subject: e.target.value })}
                                data-testid="campaign-subject"
                                className="w-full bg-transparent border-b border-cp-border py-2 cp-serif text-lg outline-none"
                            />
                            <input
                                placeholder="Texte d'aperçu (optionnel)"
                                value={campForm.preview_text}
                                onChange={(e) => setCampForm({ ...campForm, preview_text: e.target.value })}
                                data-testid="campaign-preview"
                                className="w-full bg-transparent border-b border-cp-border py-2 text-sm outline-none"
                            />
                            <textarea
                                required
                                rows={6}
                                placeholder="Corps en HTML simple (<p>, <strong>, <a href=...>). Pas de <html> ni <body>, l'enveloppe Côte Perdue est ajoutée automatiquement."
                                value={campForm.html_body}
                                onChange={(e) => setCampForm({ ...campForm, html_body: e.target.value })}
                                data-testid="campaign-body"
                                className="w-full bg-transparent border border-cp-border p-3 text-sm font-mono outline-none"
                            />
                            {campMsg && <p className={`cp-tiny ${campStatus === "error" ? "text-red-700" : "text-cp-foret"}`}>{campMsg}</p>}
                            <button
                                type="submit"
                                disabled={campStatus === "loading"}
                                data-testid="campaign-create"
                                className="cp-kicker text-cp-foret border-b border-cp-foret pb-0.5 hover:text-cp-noir disabled:opacity-50"
                            >
                                {campStatus === "loading" ? "Création…" : "+ Créer un brouillon"}
                            </button>
                        </form>

                        {/* List campaigns */}
                        {campaigns.length === 0 ? (
                            <p className="text-cp-gris italic">Aucune campagne.</p>
                        ) : (
                            <ul className="divide-y divide-cp-border">
                                {campaigns.map((c) => (
                                    <li key={c.id} data-testid={`campaign-${c.id}`} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
                                        <div className="flex-1">
                                            <p className="cp-serif text-lg text-cp-noir">{c.subject}</p>
                                            <p className="cp-tiny text-cp-gris mt-1">
                                                {c.status === "sent"
                                                    ? `Envoyé le ${new Date(c.sent_at).toLocaleDateString("fr-FR")} · ${c.sent_count} envois · ${c.failed_count} échecs`
                                                    : `Brouillon · créé le ${new Date(c.created_at).toLocaleDateString("fr-FR")}`}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            {c.status === "draft" && (
                                                <>
                                                    <button
                                                        onClick={async () => {
                                                            const dry = await api.post(`/admin/newsletter/campaigns/${c.id}/send?dry_run=true`);
                                                            if (confirm(`Envoyer à ${dry.data.would_send_to} abonnés ?\nPremiers : ${dry.data.first_5.join(", ")}`)) {
                                                                await api.post(`/admin/newsletter/campaigns/${c.id}/send`);
                                                                reloadAll();
                                                            }
                                                        }}
                                                        data-testid={`campaign-send-${c.id}`}
                                                        className="cp-kicker text-cp-foret hover:text-cp-noir flex items-center gap-1"
                                                    ><Send size={12}/> ENVOYER</button>
                                                    <button
                                                        onClick={async () => { if (confirm("Supprimer ce brouillon ?")) { await api.delete(`/admin/newsletter/campaigns/${c.id}`); reloadAll(); } }}
                                                        data-testid={`campaign-delete-${c.id}`}
                                                        className="text-red-700 hover:opacity-70"
                                                    ><Trash2 size={12}/></button>
                                                </>
                                            )}
                                            {c.status === "sent" && <span className="cp-kicker text-[9px] text-cp-foret">ENVOYÉ ✓</span>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    {/* ===== CREATE EXPERIENCE / PARCOURS / EVENT ===== */}
                    <AdminContentCreator />

                    {/* ===== AUTO-DISCOVERY (weekly Google Places scan) ===== */}
                    <AdminDiscovery />

                    {/* ===== EVENT SUBMISSIONS (public form moderation) ===== */}
                    <AdminEventSubmissions />

                    {/* ===== NEWSLETTER ONBOARDING TEMPLATES ===== */}
                    <AdminOnboarding />
                </div>
            </main>
        </div>
    );
}
